/**
 * File: fomo-widget.js
 * Purpose: FOMO ticker widget — forward-flowing live feed.
 * New items appear at TOP, old items exit at bottom.
 * Fetches 20 events, displays 6. Pure-Tailwind + inline SVG output (no separate CSS or webfont).
 */
(function () {
  'use strict';

  const VISIBLE_COUNT = 6;
  const TICK_INTERVAL = 4500;
  const POLL_INTERVAL = 120000;
  const WIDGET_SELECTOR = '.bfwt-fomo-widget';
  const API_URL =
    (window.bfwtVars?.restUrl || '/wp-json/') + 'app-api/v1/fomo/events';

  // Item Tailwind classes — must match the SSR output in UnifiedIntelWidget.php.
  const ITEM_CLS =
    'bfwt-fomo-item tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-2xl tw-bg-ink/[.025] tw-border tw-border-line/50 tw-transition-all tw-duration-300 hover:tw-bg-ink/[.04] hover:tw-border-line/80';

  // Inline SVG icons matching bfwt_icon() PHP helper so JS ticks never render raw ligature text.
  const FOMO_ICON_SVGS = {
    content_copy:
      '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" style="width:1em;height:1em;display:block;"><rect x="8" y="8" width="12" height="12" rx="2.5" stroke="currentColor" stroke-width="1.85" stroke-dasharray="3 2"/><path d="M16 8V6.2C16 5.0 15.0 4 13.8 4H6.2C5.0 4 4 5.0 4 6.2V13.8C4 15.0 5.0 16 6.2 16H8" stroke="currentColor" stroke-width="1.85" stroke-linecap="round"/></svg>',
    local_offer:
      '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" style="width:1em;height:1em;display:block;"><path d="M12.6 3H6.2C4.98 3 4 3.98 4 5.2V11.6C4 12.22 4.25 12.81 4.69 13.25L11.75 20.31C12.63 21.19 14.06 21.19 14.94 20.31L20.31 14.94C21.19 14.06 21.19 12.63 20.31 11.75L13.25 4.69C12.81 4.25 12.22 4 11.6 4" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round"/><circle cx="8.25" cy="8.25" r="1.35" fill="currentColor"/><path d="M14 9.5L15.1 11.9L17.5 13L15.1 14.1L14 16.5L12.9 14.1L10.5 13L12.9 11.9L14 9.5Z" fill="var(--bf-icon-emerald, currentColor)"/></svg>',
    bolt:
      '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" style="width:1em;height:1em;display:block;"><path d="M13.2 3.2L5.5 13.2H11.8L10.8 20.8L18.5 10.8H12.2L13.2 3.2Z" fill="var(--bf-icon-gold, currentColor)" fill-opacity="0.16" stroke="currentColor" stroke-width="1.85" stroke-linejoin="round"/></svg>',
    verified:
      '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" style="width:1em;height:1em;display:block;"><path d="M12 2.8L14.6 4.5L17.7 4.2L18.8 7.1L21.2 9.1L20.1 12L21.2 14.9L18.8 16.9L17.7 19.8L14.6 19.5L12 21.2L9.4 19.5L6.3 19.8L5.2 16.9L2.8 14.9L3.9 12L2.8 9.1L5.2 7.1L6.3 4.2L9.4 4.5L12 2.8Z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M9.2 12.2L11.1 14.1L15.1 10.1" stroke="var(--bf-icon-emerald, currentColor)" stroke-width="2.05" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    public:
      '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" style="width:1em;height:1em;display:block;"><circle cx="12" cy="12" r="8.8" stroke="currentColor" stroke-width="1.85"/><path d="M3.5 12H20.5M12 3.2C14.5 5.8 15.8 8.8 15.8 12C15.8 15.2 14.5 18.2 12 20.8C9.5 18.2 8.2 15.2 8.2 12C8.2 8.8 9.5 5.8 12 3.2Z" stroke="currentColor" stroke-width="1.7"/></svg>',
  };

  function renderFomoIcon(name) {
    const svg = FOMO_ICON_SVGS[name] || FOMO_ICON_SVGS.public;
    return `<span class="bf-icon notranslate material-symbols-rounded tw-inline-flex tw-items-center tw-justify-center tw-shrink-0 tw-text-base tw-leading-none" translate="no" aria-hidden="true">${svg}</span>`;
  }

  let allEvents = [];
  let currentIndex = 0;
  let tickTimer = null;
  let pollTimer = null;
  let isVisible = false;

  async function fetchEvents() {
    try {
      const response = await fetch(`${API_URL}?limit=20&_=${Date.now()}`);
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      if (!Array.isArray(data) || data.length === 0) {
        return;
      }
      allEvents = data;
      const widget = document.querySelector(WIDGET_SELECTOR);
      if (widget) {
        renderCurrentSlice(widget);
      }
    } catch {
      /* silent */
    }
  }

  function renderCurrentSlice(widget) {
    const list = widget.querySelector('.bfwt-fomo-list');
    if (!list || allEvents.length === 0) {
      return;
    }

    const slice = [];
    for (let i = 0; i < VISIBLE_COUNT && i < allEvents.length; i++) {
      slice.push(allEvents[(currentIndex + i) % allEvents.length]);
    }

    list.innerHTML = slice.map((ev) => buildItemHTML(ev)).join('');
  }

  function buildItemHTML(event) {
    const icon = event.icon || 'public';
    const brandSlug = event.brand_slug || '';
    const brand = event.brand || '';
    const itemUrl =
      event.url || (brandSlug ? `/brand/${escapeHtml(brandSlug)}/` : '');

    let displayText = escapeHtml(decodeEntities(event.text));
    const escapedBrand = escapeHtml(decodeEntities(brand));

    if (itemUrl && escapedBrand) {
      const brandLink = `<a href="${escapeHtml(itemUrl)}" class="u-text-neon hover:tw-underline">${escapedBrand}</a>`;
      displayText = displayText.replace(escapedBrand, brandLink);
    } else if (escapedBrand) {
      displayText = displayText.replace(
        escapedBrand,
        `<span class="u-text-neon">${escapedBrand}</span>`
      );
    }

    return `<li class="${ITEM_CLS}">
            <span class="tw-flex tw-items-center tw-justify-center tw-w-10 tw-h-10 tw-rounded-xl tw-bg-paper tw-border tw-border-line/60 tw-text-accent tw-shrink-0 tw-shadow-sm">
                ${renderFomoIcon(icon)}
            </span>
            <span class="tw-flex-1 tw-min-w-0 tw-text-sm tw-font-medium tw-text-ink tw-leading-snug">
                ${displayText}
                <span class="tw-block tw-text-[0.65rem] tw-font-bold tw-uppercase tw-tracking-wider tw-text-whisper tw-mt-1">${escapeHtml(event.time_ago)}</span>
            </span>
        </li>`;
  }

  function tick() {
    if (allEvents.length <= VISIBLE_COUNT || !isVisible) {
      return;
    }

    const widget = document.querySelector(WIDGET_SELECTOR);
    if (!widget) {
      return;
    }
    const list = widget.querySelector('.bfwt-fomo-list');
    if (!list) {
      return;
    }

    const newEventIndex = (currentIndex + VISIBLE_COUNT) % allEvents.length;
    currentIndex = (currentIndex + 1) % allEvents.length;
    const newEvent = allEvents[newEventIndex];

    const lastItem = list.querySelector('.bfwt-fomo-item:last-child');
    if (lastItem) {
      lastItem.style.opacity = '0';
      lastItem.style.transform = 'translateY(8px)';
    }

    setTimeout(() => {
      if (lastItem) {
        lastItem.remove();
      }

      const temp = document.createElement('div');
      temp.innerHTML = buildItemHTML(newEvent);
      const newItem = temp.firstElementChild;

      newItem.style.opacity = '0';
      newItem.style.transform = 'translateY(-10px)';

      list.insertBefore(newItem, list.firstChild);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          newItem.style.opacity = '';
          newItem.style.transform = '';
        });
      });
    }, 300);
  }

  function startTicker() {
    if (tickTimer) {
      return;
    }
    tickTimer = setInterval(tick, TICK_INTERVAL);
  }

  function stopTicker() {
    if (tickTimer) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
  }

  function init() {
    const widget = document.querySelector(WIDGET_SELECTOR);
    if (!widget) {
      return;
    }

    if (!('IntersectionObserver' in window)) {
      isVisible = true;
      fetchEvents();
      startTicker();
      if (!pollTimer) {
        pollTimer = setInterval(fetchEvents, POLL_INTERVAL);
      }
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!isVisible) {
              isVisible = true;
              fetchEvents();
              startTicker();
              if (!pollTimer) {
                pollTimer = setInterval(fetchEvents, POLL_INTERVAL);
              }
            }
          } else {
            isVisible = false;
            stopTicker();
            if (pollTimer) {
              clearInterval(pollTimer);
              pollTimer = null;
            }
          }
        });
      },
      { rootMargin: '100px' }
    );

    observer.observe(widget);
  }

  function escapeHtml(text) {
    if (!text) {
      return '';
    }
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  let taInstance = null;
  function decodeEntities(text) {
    if (!text) {
      return '';
    }
    if (!taInstance) {
      taInstance = document.createElement('textarea');
    }
    taInstance.innerHTML = String(text);
    return taInstance.value;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
